import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Button,
  Grid,
  Paper,
  Alert
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import StarRating from './StarRating';
import ReviewForm from './ReviewForm';
import reviewService, { ReviewStats, Review } from '../../services/reviewService';

interface ReviewSummaryProps {
  productId: number;
  productTitle: string;
  onReviewAdded?: () => void;
}

const ReviewSummary: React.FC<ReviewSummaryProps> = ({
  productId,
  productTitle,
  onReviewAdded
}) => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [userReview, setUserReview] = useState<Review | null>(null);
  const [reviewFormOpen, setReviewFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load review stats
      const statsData = await reviewService.getProductReviewStats(productId);
      setStats(statsData);

      // Load user's review if logged in
      if (user) {
        const review = await reviewService.getUserProductReview(productId);
        setUserReview(review);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [productId, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleReviewSuccess = (review: Review) => {
    setUserReview(review);
    loadData(); // Reload stats
    if (onReviewAdded) {
      onReviewAdded();
    }
  };

  const getRatingPercentage = (rating: number): number => {
    if (!stats || stats.totalReviews === 0) return 0;
    return (stats.ratingDistribution[rating as keyof typeof stats.ratingDistribution] / stats.totalReviews) * 100;
  };

  if (loading || !stats) {
    return null;
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
              {(stats.averageRating || 0).toFixed(1)}
            </Typography>
            <StarRating rating={stats.averageRating || 0} size="large" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('reviews.basedOn', { count: stats.totalReviews })}
            </Typography>
            
            {user && !userReview && (
              <Button
                variant="contained"
                onClick={() => setReviewFormOpen(true)}
                sx={{ mt: 2 }}
                fullWidth
              >
                {t('reviews.writeReview')}
              </Button>
            )}
            
            {!user && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('reviews.loginToReview')}
              </Typography>
            )}
          </Box>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              {t('reviews.ratingBreakdown')}
            </Typography>
            
            {[5, 4, 3, 2, 1].map((rating) => (
              <Box key={rating} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ minWidth: 20 }}>
                  {rating}
                </Typography>
                <StarIcon sx={{ fontSize: 16, color: '#FFB400', mx: 0.5 }} />
                <Box sx={{ flex: 1, mx: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={getRatingPercentage(rating)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#E0E0E0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#FFB400'
                      }
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'right' }}>
                  {stats.ratingDistribution[rating as keyof typeof stats.ratingDistribution]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>

      {userReview && userReview.status === 'PENDING' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('reviews.yourReviewPending')}
        </Alert>
      )}

      {userReview && userReview.status === 'REJECTED' && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('reviews.yourReviewRejected')}
        </Alert>
      )}

      <ReviewForm
        open={reviewFormOpen}
        onClose={() => setReviewFormOpen(false)}
        productId={productId}
        productTitle={productTitle}
        onSuccess={handleReviewSuccess}
      />
    </Paper>
  );
};


export default ReviewSummary;